
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

type CostType = 'regular' | 'night' | 'holiday';

// Create a cache to store already fetched costs
const costCache: Record<string, { cost: number | null, timestamp: number }> = {};

// Helper function to validate UUID format
const isValidUUID = (uuid: string | null | undefined): boolean => {
  if (!uuid) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
};

export const useGuardHourlyCost = (guardId: string | null | undefined, costType: CostType = 'regular') => {
  const [hourlyCost, setHourlyCost] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Create a cache key
  const cacheKey = useMemo(() => {
    if (!guardId) return '';
    return `${guardId}-${costType}`;
  }, [guardId, costType]);

  useEffect(() => {
    const fetchHourlyCost = async () => {
      // Validate that guardId is null, undefined, or a valid UUID
      if (guardId === null || guardId === undefined) {
        console.log("Guard ID is null or undefined, skipping hourly cost fetch");
        setHourlyCost(null);
        setIsLoading(false);
        return;
      }
      
      // Check if guardId is a valid UUID
      if (!isValidUUID(guardId)) {
        console.error(`Invalid UUID format for guard ID: ${guardId}`);
        setError(`Invalid UUID format for guard ID: ${guardId}`);
        setHourlyCost(null);
        setIsLoading(false);
        return;
      }

      // Check if we have a cached value that's less than 5 minutes old
      const cachedValue = costCache[cacheKey];
      if (cachedValue && Date.now() - cachedValue.timestamp < 5 * 60 * 1000) {
        console.log(`Using cached hourly cost for ${guardId} (${costType}):`, cachedValue.cost);
        setHourlyCost(cachedValue.cost);
        setIsLoading(false);
        setError(null);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        console.log(`Fetching ${costType} hourly cost for guard: ${guardId}`);
        
        // First fetch the guard's trietia and their admin from profiles
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('trietia, created_by')
          .eq('id', guardId)
          .single();
          
        if (profileError) {
          console.error(`Error fetching profile data for guard ${guardId}:`, profileError);
          setHourlyCost(null);
          setIsLoading(false);
          setError(`Failed to fetch profile data: ${profileError.message}`);
          return;
        }
        
        // Handle case where trietia is null or undefined
        if (profileData?.trietia === null || profileData?.trietia === undefined) {
          console.log(`Guard ${guardId} has no trietia value defined`);
          setHourlyCost(null);
          setIsLoading(false);
          
          // Cache the null result
          costCache[cacheKey] = { cost: null, timestamp: Date.now() };
          return;
        }

        // Get the admin who created this guard (for admin-specific costs)
        const guardAdminId = profileData.created_by;
        if (!guardAdminId) {
          console.error(`Guard ${guardId} has no created_by admin`);
          setHourlyCost(null);
          setIsLoading(false);
          setError('Guard has no associated admin');
          return;
        }
        
        console.log(`Guard ${guardId} trietia value:`, profileData.trietia, `admin:`, guardAdminId);
        
        // Convert trietia value to number if it's a string
        const trietiaNumber = typeof profileData.trietia === 'string' 
          ? parseInt(profileData.trietia, 10)
          : profileData.trietia;
        
        if (isNaN(trietiaNumber)) {
          console.log(`Invalid trietia value: ${profileData.trietia}`);
          setHourlyCost(null);
          setIsLoading(false);
          
          // Cache the null result
          costCache[cacheKey] = { cost: null, timestamp: Date.now() };
          return;
        }

        console.log(`Fetching ${costType} hourly cost for trietia ${trietiaNumber} and admin ${guardAdminId}`);

        const tableName = costType === 'night' 
          ? 'night_hourly_costs' 
          : costType === 'holiday' 
            ? 'holiday_hourly_costs' 
            : 'hourly_costs';
        
        const { data, error } = await supabase
          .from(tableName)
          .select("cost_per_hour")
          .eq("trietia", trietiaNumber)
          .eq("admin_id", guardAdminId)
          .maybeSingle();
          
        if (error) {
          console.error(`Error fetching ${costType} hourly cost for trietia ${trietiaNumber}:`, error);
          setError(`Failed to load ${costType} hourly cost data: ${error.message}`);
          setHourlyCost(null);
        } else {
          const cost = data?.cost_per_hour || null;
          console.log(`Retrieved hourly cost for trietia ${trietiaNumber}:`, cost);
          setHourlyCost(cost);
          
          // Cache the result
          costCache[cacheKey] = { cost, timestamp: Date.now() };
        }
      } catch (error: any) {
        console.error(`Error fetching ${costType} hourly cost:`, error);
        setError(error.message || `Failed to load ${costType} hourly cost data`);
        setHourlyCost(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHourlyCost();
  }, [guardId, costType, cacheKey]);

  return { hourlyCost, isLoading, error };
};
